import { fetchApi } from '@inmagik/react-crud'
import { PartialWithId } from '@inmagik/react-crud/dist/types'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { FormikHelpers } from 'formik'
import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import * as yup from 'yup'
import { useAuth } from '../../auth'
import MaterialIcon from '../../components/MaterialIcon'
import ModalConfirm from '../../components/ModalConfirm'
import ModalForm from '../../components/ModalForm'
import Paginator from '../../components/Paginator'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import TabMenu from '../../components/TabMenu'
import { API_URL, USER_ROLES, USER_ROLES_CLIENTI, USER_ROLES_UTENTI } from '../../constants'
import { useQsFilters } from '../../hooks/filters'
import {
  useChangeExpirationMultipleUtenti,
  useCreateUtente,
  useDeleteUtente,
  usePartialUpdateUtente,
  useUpdateUtente,
  useUtentiList,
} from '../../hooks/utenti'
import { User, UserRoleKeyType } from '../../types'
import { useIsCliente, useIsOnlyCliente } from '../../utils/utils'
import FormChangeExpiration from './FormChangeExpiration'
import FormUtente from './FormUtente'
import FormUtenteCliente from './FormUtenteCliente'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? 'ragione_sociale',
  roles: params.getAll('roles').filter(Boolean) as UserRoleKeyType[],
})

const initialValuesUtente = {
  full_name: '',
  email: '',
}
const initialValuesCliente = {
  full_name: '',
  email: '',
  clienti: [],
  nome_holding: '',
}
const initialValuesChangeExpiration = {
  expiration_date: '',
}

const schemaUtente = yup.object().shape({
  full_name: yup.string().required().label('Nome e Cognome'),
  email: yup.string().email().required().label('Email'),
  role: yup.string().required().label('Ruolo'),
})

const schemaCliente = yup.object().shape({
  full_name: yup.string().required().label('Nome e Cognome'),
  email: yup.string().email().required().label('Email'),
  role: yup.string().required().label('Ruolo'),
  clienti: yup.array().of(yup.number()).min(1, 'Seleziona almeno un cliente').label('Aziende'),
})

const schemaChangeExpiration = yup.object().shape({
  expiration_date: yup.date().required().label('Data di scadenza'),
})

export default function Utenti() {
  const { tokens, user } = useAuth()
  const isCliente = useIsCliente()
  const isOnlyCliente = useIsOnlyCliente()
  const { filters, uiFilters, setFilters } = useQsFilters(initFilters)
  const [tab, setTab] = useState<'utenti' | 'clienti'>(!isCliente ? 'utenti' : 'clienti')

  const roles = useMemo(() => {
    const tabRoles: UserRoleKeyType[] =
      tab === 'utenti'
        ? Object.values(USER_ROLES_UTENTI).map((role) => role.value)
        : Object.values(USER_ROLES_CLIENTI).map((role) => role.value)
    const filteredRoles = filters.roles.filter((role: UserRoleKeyType) => tabRoles.includes(role))
    return filteredRoles.length > 0 ? filteredRoles : tabRoles
  }, [filters.roles, tab])

  const {
    data: utenti,
    isLoading,
    refetch,
  } = useUtentiList({
    search: filters.search,
    ordering: filters.ordering,
    page: String(filters.page),
    roles: roles.join(','),
  })

  const [modalStateForm, modalActionsForm] = useModalTrigger<User>()
  const [modalStateResendEmail, modalActionsResendEmail] = useModalTrigger<User>()
  const [modalStateMultipleResendEmail, modalActionsMultipleResendEmail] = useModalTrigger<User>()
  const [modalStateMultipleChangeExpiration, modalActionsMultipleChangeExpiration] = useModalTrigger<User>()

  const [modalConfirmState, modalConfirmActions] = useModalTrigger()

  const [users, setUsers] = useState<User[]>([])

  const { mutateAsync: createUser, status: createUserStatus } = useCreateUtente()
  // const { mutateAsync: deleteUser, status: deleteUserStatus } =
  //   useDeleteUtente()
  const { mutateAsync: updateUser, status: updateUserStatus } = useUpdateUtente()
  const { mutateAsync: changeExpirationMultiple } = useChangeExpirationMultipleUtenti()

  const { mutateAsync: deleteUser } = useDeleteUtente()

  const saveCustomer = useCallback(
    (utente: PartialWithId<User>, actions: FormikHelpers<User>) => {
      if (utente.id) {
        return updateUser(utente)
          .then(() => {
            modalActionsForm.close()
            actions.resetForm()
          })
          .catch((e) => {
            actions.setErrors(e)
          })
      } else {
        return createUser(utente)
          .then(() => {
            modalActionsForm.close()
            actions.resetForm()
          })
          .catch((e) => {
            actions.setErrors(e.data)
          })
      }
    },
    [modalActionsForm, modalStateForm, updateUser, createUser]
  )

  const [openMoreUtente, setOpenMoreUtente] = useState<number | null>(null)
  const [modalStateDelete, modalActionsDelete] = useModalTrigger<User>()

  const changeExpiration = useCallback(
    (values: { expiration_date: Date | string | null }, actions: FormikHelpers<User>) => {
      const data = {
        ids: users
          .filter((u) => u.role === USER_ROLES.cliente.value || u.role === USER_ROLES.responsabile_cliente.value)
          .map((u) => u.id),
        expiration_date: values.expiration_date,
      }
      changeExpirationMultiple(data)
        .then(() => {
          modalActionsMultipleChangeExpiration.close()
          setUsers([])
          refetch()
        })
        .catch((e) => {
          actions.setErrors(e.data)
        })
    },
    [changeExpirationMultiple, modalActionsMultipleChangeExpiration, users, refetch]
  )

  // if is open more bilancia close it when click outside using id="bilancia-{bilancia.id}" avoiding to close it when click on the button

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof Element && !e.target.closest(`#utente-${openMoreUtente}`)) {
        setOpenMoreUtente(null)
      }
    }
    document.addEventListener('click', handleClick)
    return () => {
      document.removeEventListener('click', handleClick)
    }
  }, [openMoreUtente])

  const { mutateAsync: updatePartialUtente } = usePartialUpdateUtente()

  return (
    <div className="page">
      <div className="row mt-4">
        <div className="col-12 d-flex justify-content-between align-items-center">
          <h1 className="title">Utenti</h1>
          <div>
            {!isOnlyCliente && (
              <Button
                onClick={() => {
                  modalActionsForm.open()
                }}
                variant="secondary"
                className="text-white d-flex align-items-center"
                type="button"
              >
                <span>Crea utente</span>
                <MaterialIcon icon="add" className="ms-2" />
              </Button>
            )}
          </div>
        </div>
        {!isCliente && (
          <TabMenu
            classNameContainer="pb-3"
            tabs={[
              { label: 'Utenti', value: 'utenti' },
              { label: 'Clienti', value: 'clienti' },
            ]}
            activeTab={tab}
            onTabChange={(value: 'utenti' | 'clienti') => {
              setUsers([])
              setTab(value)
            }}
          />
        )}
        <div className="col-12">
          <div className="bg-white p-2 border-bottom d-flex justify-content-between align-items-center w-100 rounded">
            <div className="d-flex align-items-center gap-2 ms-2">
              <input
                type="text"
                style={{ width: '500px' }}
                className="form-control"
                placeholder="Cerca utente..."
                value={uiFilters.search}
                onChange={(e) => setFilters({ search: e.target.value, page: 1 })}
              />
              <div className="d-flex align-items-center ms-3">
                <label className="label-m text-body-secondary me-2">Ruolo</label>
                <select
                  className={classNames('form-select me-2', {
                    'text-secondary':
                      tab === 'utenti'
                        ? USER_ROLES_UTENTI.map((m) => m.value).some((role) => filters.roles.includes(role))
                        : USER_ROLES_CLIENTI.map((m) => m.value).some((role) => filters.roles.includes(role)),
                  })}
                  style={{ width: '170px' }}
                  value={filters.roles[0] ?? ''}
                  onChange={(e) => setFilters({ roles: [e.target.value as UserRoleKeyType], page: 1 })}
                >
                  <option value="">Scegli il ruolo</option>
                  {Object.values(tab === 'utenti' ? USER_ROLES_UTENTI : USER_ROLES_CLIENTI).map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="me-2">
              Stai visualizzando {utenti?.count ?? 0} di {utenti?.full_count ?? 0} utenti
            </div>
          </div>
          {!isOnlyCliente && users.length > 0 && (
            <div className="bg-secondary py-2 px-4 text-white border-bottom d-flex justify-content-between align-items-center w-100 rounded">
              <div className="d-flex align-items-center">
                <div>{users.length} utenti selezionati</div>
                <div className="d-flex align-items-center ms-3">
                  <Button
                    variant="outline-secondary"
                    className="d-flex align-items-center bg-white text-secondary ms-2"
                    onClick={() => {
                      modalActionsMultipleResendEmail.open()
                    }}
                  >
                    Invita utenti
                    <span className="material-icons ms-2">moving</span>
                  </Button>
                  {tab === 'clienti' && (
                    <Button
                      variant="outline"
                      className="d-flex align-items-center bg-white text-secondary ms-2"
                      onClick={() => {
                        modalActionsMultipleChangeExpiration.open()
                      }}
                    >
                      Modifica Scadenza
                      <span className="material-icons ms-2">edit_calendar</span>
                    </Button>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center">
                <Button
                  variant="outline-danger"
                  className="d-flex align-items-center bg-white text-danger"
                  onClick={() => {
                    modalConfirmActions.open()
                  }}
                >
                  <span className="material-icons me-2">delete</span>
                  Elimina utenti
                </Button>
              </div>
            </div>
          )}
          <StickyTable
            className={users.length > 0 ? 'StickyWithFiltersAndSede' : !isCliente ? 'StickyWithFilters' : ''}
            isLoading={isLoading}
          >
            <thead className="position-sticky top-0 bg-white">
              <Sorter
                value={uiFilters.ordering}
                onSortChange={(ordering) =>
                  setFilters({
                    ordering,
                  })
                }
              >
                <tr>
                  <th>
                    <div className="d-flex align-items-center">
                      Nome e Cognome
                      <SortControl field="full_name" />
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-items-center">
                      Email
                      <SortControl field="email" />
                    </div>
                  </th>
                  <th>
                    <div className="d-flex align-items-center">
                      Ruolo
                      <SortControl field="role" />
                    </div>
                  </th>
                  {tab === 'clienti' && (
                    <>
                      <th>
                        <div className="d-flex align-items-center">
                          Holding
                          <SortControl field="nome_holding" />
                        </div>
                      </th>
                      <th>
                        <div className="d-flex align-items-center">Aziende</div>
                      </th>
                      <th>
                        <div className="d-flex align-items-center">
                          Data scadenza
                          <SortControl field="expiration_date" />
                        </div>
                      </th>
                    </>
                  )}
                  <th>
                    <div className="d-flex align-items-center">
                      Ultimo accesso
                      <SortControl field="last_login" />
                    </div>
                  </th>
                </tr>
              </Sorter>
            </thead>
            <tbody>
              {utenti?.results?.map((utente) => (
                <tr key={utente.id}>
                  {!isOnlyCliente ? (
                    <td className="d-flex align-items-center">
                      <div>
                        <input
                          type="checkbox"
                          checked={users.includes(utente)}
                          onChange={() => {
                            if (users.includes(utente)) {
                              setUsers(users.filter((user) => user !== utente))
                            } else {
                              setUsers([...users, utente])
                            }
                          }}
                        />
                      </div>
                      <div className="p-md text-body-secondary ms-2">{utente.full_name}</div>
                      <div className="ms-3">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation()
                            if (openMoreUtente === utente.id) {
                              setOpenMoreUtente(null)
                            } else {
                              setOpenMoreUtente(utente.id)
                            }
                          }}
                          variant="light"
                          className="text-secondary d-flex align-items-center"
                          type="button"
                        >
                          <span className="material-icons">more_horiz</span>
                        </Button>
                        {openMoreUtente === utente.id && (
                          <div
                            id={`utente-${utente.id}`}
                            className="list-group position-absolute"
                            // ref={editDeleteRef}
                            style={{
                              // zIndex: 1,
                              width: '200px',
                              boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
                              borderRadius: '5px',
                            }}
                          >
                            <button
                              onClick={() => {
                                modalActionsForm?.open(utente)
                              }}
                              className="list-group-item list-group-item-action d-flex align-items-center"
                            >
                              <span
                                className="material-icons me-2"
                                style={{
                                  fontSize: '16px',
                                }}
                              >
                                edit
                              </span>
                              Modifica
                            </button>
                            <button
                              onClick={() => {
                                modalActionsDelete?.open(utente)
                              }}
                              className="list-group-item list-group-item-action d-flex align-items-center text-danger"
                            >
                              <span
                                className="material-icons me-2"
                                style={{
                                  fontSize: '16px',
                                }}
                              >
                                delete
                              </span>
                              Elimina
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  ) : (
                    <td className="p-md text-body-secondary ms-2">{utente.full_name}</td>
                  )}

                  <td className="p-md text-body-secondary">{utente.email}</td>
                  <td className="text-capitalize p-md text-body-secondary">{USER_ROLES[utente.role].label}</td>
                  {tab === 'clienti' && (
                    <>
                      <td className="text-capitalize p-md text-body-secondary">{utente.nome_holding}</td>
                      <td className="text-capitalize p-md text-body-secondary">
                        {utente.clienti_data?.map((c) => c.ragione_sociale).join(', ')}
                      </td>
                      <td className="text-capitalize p-md text-body-secondary">
                        {utente.expiration_date ? dayjs(utente.expiration_date).format('DD/MM/YYYY') : 'N/A'}
                      </td>
                    </>
                  )}
                  <td className="p-md text-body-secondary">
                    {utente.last_login && dayjs(utente.last_login).format('DD/MM/YYYY HH:mm')}
                    {!utente.last_login && !isOnlyCliente && (
                      <Button
                        onClick={() => {
                          modalActionsResendEmail.open(utente)
                        }}
                        size="sm"
                        variant="secondary"
                        className="text-white d-flex align-items-center"
                        type="button"
                      >
                        Reinvita utente
                        <span
                          className="material-icons ms-2"
                          style={{
                            fontSize: 16,
                          }}
                        >
                          send
                        </span>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </StickyTable>

          <Paginator
            count={utenti?.count ?? 0}
            currentPage={uiFilters.page}
            goToPage={(page) => setFilters({ page })}
          />
        </div>
      </div>
      <Modal
        show={modalStateResendEmail.isOpen}
        onHide={modalActionsResendEmail.toggle}
        contentClassName="bg-topbar"
        centered
      >
        <Modal.Header>
          <Modal.Title>Reinvia email di attivazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="p-2">
            <p>Stai per reinviare l'email di attivazione a {modalStateResendEmail.value?.email}</p>
            <p>Sei sicuro di voler procedere?</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              fetchApi(`${API_URL}/utenti/users/${modalStateResendEmail.value?.id}/resend_activation_email/`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${tokens?.access}`,
                },
              }).then(() => {
                modalActionsResendEmail.close()
              })
            }}
            variant="secondary"
            className="text-white d-flex align-items-center"
          >
            Reinvia email
            <span className="material-icons ms-2">send</span>
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal
        show={modalStateMultipleResendEmail.isOpen}
        onHide={modalActionsMultipleResendEmail.toggle}
        contentClassName="bg-topbar"
        centered
      >
        <Modal.Header>
          <Modal.Title>Reinvia email di attivazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="p-2">
            <p>Stai per reinviare l'email di attivazione a {users.length} utenti</p>
            <p>Sei sicuro di voler procedere?</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              await fetchApi(`${API_URL}/utenti/users/resend_activation_email_multiple/`, {
                method: 'POST',
                body: JSON.stringify({
                  ids: users.map((u) => u.id),
                }),
                headers: {
                  Authorization: `Bearer ${tokens?.access}`,
                  'Content-Type': 'application/json',
                },
              }).then(() => {
                modalActionsMultipleResendEmail.close()
                setUsers([])
              })
            }}
            variant="secondary"
            className="text-white d-flex align-items-center"
          >
            Reinvia email
            <span className="material-icons ms-2">send</span>
          </Button>
        </Modal.Footer>
      </Modal>
      <ModalForm<User>
        title={'Modifica scadenza'}
        isOpen={modalStateMultipleChangeExpiration.isOpen}
        toggle={modalActionsMultipleChangeExpiration.toggle}
        initialValues={
          users.filter((u) => u.role === USER_ROLES.cliente.value || u.role === USER_ROLES.responsabile_cliente.value)
            .length > 0
            ? { expiration_date: users[0]?.expiration_date }
            : initialValuesChangeExpiration
        }
        save={async (record, actions) => {
          const values = { expiration_date: record.expiration_date ?? null }
          return changeExpiration(values, actions)
        }}
        schema={schemaChangeExpiration}
      >
        <FormChangeExpiration />
      </ModalForm>
      <ModalForm<User>
        title={modalStateForm.value ? 'Modifica utente' : 'Crea utente'}
        isOpen={modalStateForm.isOpen}
        toggle={modalActionsForm.toggle}
        initialValues={
          modalStateForm.value ?? {
            ...(tab === 'utenti' ? initialValuesUtente : initialValuesCliente),
            ...(isCliente ? { nome_holding: user?.nome_holding, clienti: user?.clienti } : {}),
            role: tab === 'utenti' ? USER_ROLES.operatore.value : USER_ROLES.responsabile_cliente.value,
          }
        }
        save={saveCustomer}
        schema={tab === 'utenti' ? schemaUtente : schemaCliente}
      >
        {tab === 'utenti' ? <FormUtente /> : <FormUtenteCliente />}
      </ModalForm>
      <ModalConfirm
        confirmText="Elimina"
        cancelText="Annulla"
        isOpen={modalConfirmState.isOpen}
        toggle={modalConfirmActions.toggle}
        title="Elimina utenti"
        action={async () => {
          await fetchApi(`${API_URL}/utenti/users/delete_multiple/`, {
            method: 'POST',
            body: JSON.stringify({
              ids: users.map((u) => u.id),
            }),
            headers: {
              Authorization: `Bearer ${tokens?.access}`,
              'Content-Type': 'application/json',
            },
          }).then(() => {
            modalConfirmActions.close()
            setUsers([])
            refetch()
          })
        }}
        body={'Sei sicuro di voler eliminare ' + users.length + ' utenti?'}
      />
      {modalStateDelete.isOpen && (
        <ModalConfirm
          isOpen={modalStateDelete.isOpen}
          toggle={modalActionsDelete.toggle}
          title="Elimina utente"
          confirmText="Elimina"
          body={"Sei sicuro di voler eliminare l'utente " + modalStateDelete.value?.full_name + '?'}
          action={async () => {
            await updatePartialUtente({
              id: modalStateDelete.value?.id,
              is_active: false,
              cancellato: true,
            })
            modalActionsDelete.close()
            refetch()
          }}
        />
      )}
    </div>
  )
}
